/**
 * Legislation tab (§2.4, screenshots/02; design Meeting v2 — Legislation tab vpHXI): "Decided at this meeting" cards,
 * then "Introduced at this meeting" rows with a filter (All · Key legislation · Moved since meeting), each with its latest
 * action, sponsors, the 5-step timeline and a follow bell (BillRow), then the agenda's other records in the same rows.
 */
import { useState } from "react";
import { Bell, BellRing, Play } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { useMeeting } from "../../stores/meetingStore";
import { findFollow, followsStore, useFollows } from "../../stores/followsStore";
import { List, ListRow, RowActions, RowMain, RowMeta, RowTitle, RowTrail } from "../shared/List";
import { clock } from "../../lib/format";
import { seekTo } from "./vimeoClock";
import { BillRow } from "./BillRow";
import { introducedFilter, type IntroFilter } from "../../legislation/stages";
import type { AgendaChapter } from "../../types";
import type { Outcome } from "../../realtime/protocol";
import { onTablistKey } from "../../hooks/useTabs";
import { legislationHref } from "../../app/viewer";

const RESULT: Record<Outcome["result"], { label: string; tone: string }> = {
  passed: { label: "Passed", tone: "positive" },
  failed: { label: "Failed", tone: "negative" },
  not_called: { label: "Not called", tone: "warning" },
  pending: { label: "Pending", tone: "neutral" },
};

function DecidedCard({ o }: { o: Outcome }) {
  const r = RESULT[o.result];
  const voted = o.yes + o.no > 0;
  return (
    <article className="decided-card">
      <div className="decided-top">
        <div className="decided-result">
          <span className="decided-vote" data-empty={voted ? undefined : ""}>{voted ? `${o.yes}–${o.no}` : "—"}</span>
          <span className={`chip chip--${r.tone}`}>{r.label}</span>
        </div>
        {o.at != null && <button className="jump" onClick={() => seekTo(o.at!)}><Play size={10} />{clock(o.at)}</button>}
      </div>
      <h3 className="decided-title">{o.title ?? o.label}</h3>
      <p className="decided-meta">{o.id}{o.meta ? ` · ${o.meta}` : ""}</p>
    </article>
  );
}

const AGENDA_OUTCOME: Record<NonNullable<AgendaChapter["outcome"]>, { label: string; tone: string }> = {
  passed: { label: "Passed", tone: "positive" },
  discussed: { label: "Discussed", tone: "neutral" },
  not_called: { label: "Not called", tone: "warning" },
  introduced: { label: "Introduced", tone: "neutral" },
};

/** An agenda item with records (committee meetings mostly hear bills: no roll call, nothing introduced). */
function AgendaRow({ c }: { c: AgendaChapter }) {
  const ids = c.legislationIds ?? [];
  const followed = useFollows((s) => findFollow(s.follows, "legislation", ids[0]));
  const o = c.outcome ? AGENDA_OUTCOME[c.outcome] : null;
  return (
    <ListRow className="bill-row">
      <RowMain min={200} className="bill-text">
        <RowMeta className="bill-meta">
          {ids.map((id) => <a key={id} className="bill-id" href={legislationHref(id)}>{id}</a>)}
          {o && <span className={`chip chip--${o.tone}`}>{o.label}</span>}
        </RowMeta>
        <RowTitle as="h4" lines={3} className="bill-title" title={c.shortTitle ?? c.title}>{c.shortTitle ?? c.title}</RowTitle>
      </RowMain>
      <RowTrail><button className="jump" onClick={() => seekTo(c.start)}><Play size={10} />{clock(c.start)}</button></RowTrail>
      <RowActions>
      <button
        className="bell-btn"
        aria-pressed={!!followed}
        aria-label={followed ? `Stop following ${ids[0]}` : `Follow ${ids[0]}`}
        onClick={() => (followed ? followsStore.getState().unfollow(followed.id) : followsStore.getState().follow({ type: "legislation", refId: ids[0], label: c.shortTitle ?? c.title }))}
      >
        {followed ? <BellRing size={14} /> : <Bell size={14} />}
      </button>
      </RowActions>
    </ListRow>
  );
}

/** Every record the meeting touched, once: roll calls, introductions, then the agenda's other items. The tab's count is this. */
export function legislationCount(s: { outcomes: { id: string }[]; legislation: { id: string }[]; agenda?: { id: string }[]; chapters: AgendaChapter[] }) {
  return new Set([...s.outcomes.map((o) => o.id), ...s.legislation.map((b) => b.id), ...(s.agenda ?? []).map((b) => b.id), ...s.chapters.flatMap((c) => c.legislationIds ?? [])]).size;
}

export function LegislationTab() {
  const { outcomes, bills, agendaBills, people, meetingDate, chapters } = useMeeting(useShallow((s) => ({
    outcomes: s.outcomes, bills: s.legislation, agendaBills: s.agenda, people: s.people, meetingDate: s.meeting?.startsAt.slice(0, 10) ?? "", chapters: s.chapters,
  })));
  const listed = new Set([...outcomes.map((o) => o.id), ...bills.map((b) => b.id), ...agendaBills.map((b) => b.id)]);
  // Agenda chapters whose records the snapshot has no matter for (older snapshots, records the Clerk doesn't list yet).
  const agenda = chapters.filter((c) => (c.legislationIds ?? []).some((id) => !listed.has(id)));
  const [filter, setFilter] = useState<IntroFilter>("all");
  const { counts, shown } = introducedFilter(bills, meetingDate, filter);
  return (
    <div className="legislation-tab tab-panel" role="tabpanel">
      {outcomes.length > 0 && (
        <section className="decided">
          <div className="section-head">
            <h2 className="section-title">Decided at this meeting</h2>
            <span className="chip chip--neutral chip--medium">{outcomes.length}</span>
          </div>
          <div className="decided-cards">{outcomes.map((o) => <DecidedCard key={o.id} o={o} />)}</div>
        </section>
      )}
      {bills.length > 0 && (
        <section className="introduced">
          <div className="intro-head">
            <div className="section-head">
              <h2 className="section-title">Introduced at this meeting</h2>
              <span className="chip chip--neutral chip--medium">{bills.length}</span>
            </div>
            <div className="filter-tabs" role="tablist" aria-label="Filter introduced legislation" onKeyDown={onTablistKey}>
              {([["all", "All"], ["key", "Key legislation"], ["moved", "Moved since meeting"]] as const).map(([id, label]) => (
                <button key={id} role="tab" aria-selected={filter === id} className="filter-tab" onClick={() => setFilter(id)}>{label} {counts[id]}</button>
              ))}
            </div>
          </div>
          <List className="bill-list">{shown.map((b) => <BillRow key={b.id} bill={b} people={people} />)}</List>
        </section>
      )}
      {agendaBills.length + agenda.length > 0 && (
        <section className="introduced">
          <div className="section-head">
            <h2 className="section-title">On the agenda</h2>
            <span className="chip chip--neutral chip--medium">{agendaBills.length + agenda.length}</span>
          </div>
          <List className="bill-list">
            {agendaBills.map((b) => <BillRow key={b.id} bill={b} people={people} onJump={seekTo} />)}
            {agenda.map((c) => <AgendaRow key={c.start} c={c} />)}
          </List>
        </section>
      )}
    </div>
  );
}
